import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FiAlertTriangle,
  FiCamera,
  FiCheckCircle,
  FiClock,
  FiMapPin,
  FiNavigation,
  FiPackage,
  FiPhone,
  FiRefreshCw,
  FiSearch,
  FiTruck,
  FiUpload,
  FiUser,
  FiX
} from "react-icons/fi";
import Layout from "../Layout/Layout";
import { isAuthenticated } from "../auth";
import {
  listDeliveryOrders,
  lookupDeliveryOrder,
  updateDeliveryOrder
} from "../auth/deliveryApi";
import "./Postman.css";
import { elakayAutoText } from "../pageInside/dataElakay";


const POSTMAN_STATUSES = [
  { value: "picked_up", get label() { return elakayAutoText("h_aa7d0813d15fd1", "Collecte"); }, tone: "blue", icon: FiPackage },
  { value: "accepted", get label() { return elakayAutoText("h_3475c14802bf2e", "Accepte"); }, tone: "blue", icon: FiCheckCircle },
  { value: "in_transit", get label() { return elakayAutoText("h_7077e511761453", "En route"); }, tone: "blue", icon: FiTruck },
  { value: "out_for_delivery", get label() { return elakayAutoText("h_6c740ebaa54e89", "Prochain arret"); }, tone: "orange", icon: FiNavigation },
  { value: "attempted", get label() { return elakayAutoText("h_5826e1db8bbe46", "Tentative"); }, tone: "orange", icon: FiClock },
  { value: "delivered", get label() { return elakayAutoText("h_5c0c4cd5668cbb", "Livre"); }, tone: "green", icon: FiCheckCircle },
  { value: "exception", get label() { return elakayAutoText("h_08c257849b049b", "Incident"); }, tone: "red", icon: FiAlertTriangle },
  { value: "held", get label() { return elakayAutoText("h_e2ad9bf0d1b02b", "Retenu"); }, tone: "red", icon: FiClock }
];

const statusMeta = (value) => (
  POSTMAN_STATUSES.find((status) => status.value === value)
  || { value: "pending_pickup", label: elakayAutoText("h_4c2b255c010865", "A traiter"), tone: "gray", icon: FiClock }
);

const shortId = (value) => String(value || "").slice(-8).toUpperCase();

const customerName = (address = {}) => (
  [address.firstName, address.lastName].filter(Boolean).join(" ")
  || elakayAutoText("h_1bdd79b12628d8", "Client")
);

const addressText = (address = {}) => (
  [
    address.line1,
    address.line2,
    [address.city, address.state, address.postal_code].filter(Boolean).join(", "),
    address.country
  ].filter(Boolean).join(", ")
);

const mapsUrl = (address) => (
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressText(address))}`
);

const trackingFromScan = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    const url = new URL(raw);
    const queryKeys = [
      "tracking",
      "trackingNumber",
      "trackingnumber",
      "tracknum",
      "trknbr",
      "tLabels",
      "number",
      "pin"
    ];

    for (const key of queryKeys) {
      const tracking = url.searchParams.get(key);
      if (tracking) return tracking.trim();
    }

    const routeTracking = url.hash.match(/details\/([^/?#]+)/i);
    if (routeTracking?.[1]) return decodeURIComponent(routeTracking[1]);
  } catch {
    // Carrier barcodes normally contain the tracking number directly.
  }

  return raw;
};

const formatEventDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("fr-CA", {
    dateStyle: "short",
    timeStyle: "short"
  }).format(date);
};

const operatorLabel = (access, handlerRole) => {
  if (access?.isOwner) {
    return elakayAutoText("h_9f0034ad790760", "Accès vendeur");
  }
  if (access?.isHandler) {
    return access.handlerRole === "company"
      ? elakayAutoText("h_dabff4ad82936f", "Compagnie assignée")
      : elakayAutoText("h_857dd8148b4546", "Livreur assigné");
  }
  return handlerRole === "company"
    ? elakayAutoText("h_655685491539b8", "Accès compagnie par suivi")
    : elakayAutoText("h_3ea20373205eb0", "Accès livreur par suivi");
};

function StatusPill({ value }) {
  const meta = statusMeta(value);
  return (
    <span className={`postman-status postman-status-${meta.tone}`}>
      {meta.label}
    </span>
  );
}

function Postman({ infoSource }) {
  const { token: authToken } = isAuthenticated();
  const token = infoSource?.token || authToken;
  const routeLocation = useLocation();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const detailRef = useRef(null);
  const streamRef = useRef(null);
  const scannerTimerRef = useRef(null);
  const detectingRef = useRef(false);
  const photoRef = useRef([]);
  const routeLookupRef = useRef("");
  const routeScannerRef = useRef("");

  const routeQuery = useMemo(
    () => new URLSearchParams(routeLocation.search),
    [routeLocation.search]
  );
  const routeTracking = routeQuery.get("tracking") || "";
  const openScannerFromRoute = routeQuery.get("scan") === "1";

  const [stops, setStops] = useState([]);
  const [summary, setSummary] = useState({});
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [status, setStatus] = useState("out_for_delivery");
  const [handlerRole, setHandlerRole] = useState(
    () => window.localStorage.getItem("elakayPostmanRole") || "driver"
  );
  const [recipientName, setRecipientName] = useState("");
  const [note, setNote] = useState("");
  const [exceptionReason, setExceptionReason] = useState("");
  const [scanCode, setScanCode] = useState("");
  const [location, setLocation] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const activeStops = useMemo(() => (
    stops.filter((stop) => !["delivered", "returned", "cancelled"].includes(stop.delivery?.status))
  ), [stops]);

  useEffect(() => {
    window.localStorage.setItem("elakayPostmanRole", handlerRole);
  }, [handlerRole]);

  const mergeStop = useCallback((stop) => {
    if (!stop) return;
    setStops((current) => {
      const existingIndex = current.findIndex((item) => (
        String(item.orderId) === String(stop.orderId)
        && String(item.orderProductId) === String(stop.orderProductId)
      ));
      if (existingIndex < 0) return [stop, ...current];
      return current.map((item, index) => index === existingIndex ? stop : item);
    });
  }, []);

  const loadStops = useCallback(async (query = search, requestedStatus = statusFilter) => {
    if (!token) return null;
    setLoading(true);
    setError("");

    try {
      const data = await listDeliveryOrders(token, {
        q: query,
        status: requestedStatus,
        limit: 100
      });
      setStops(data.orders || []);
      setSummary(data.summary || {});
      return data;
    } catch (loadError) {
      setError(loadError.message || elakayAutoText("h_3e4fccf04a8009", "Impossible de charger la tournee."));
      return null;
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, token]);

  useEffect(() => {
    loadStops(search);
    // Status filter intentionally reloads the route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, token]);

  useEffect(() => () => {
    photoRef.current.forEach((photo) => URL.revokeObjectURL(photo.url));
    if (scannerTimerRef.current) clearInterval(scannerTimerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
  }, []);

  const selectStop = useCallback((stop) => {
    const currentStatus = stop.delivery?.status || "out_for_delivery";
    const selectableStatus = POSTMAN_STATUSES.some(({ value }) => value === currentStatus)
      ? currentStatus
      : "picked_up";
    setSelected(stop);
    setStatus(selectableStatus);
    setRecipientName(stop.delivery?.recipientName || customerName(stop.shippingAddress));
    setExceptionReason(stop.delivery?.exceptionReason || "");
    setNote("");
    setScanCode("");
    setLocation(null);
    setMessage("");
    setError("");
  }, []);

  const findFromScan = useCallback(async (rawValue, options = {}) => {
    const raw = String(rawValue || "").trim();
    if (!raw) return;

    const tracking = trackingFromScan(raw);
    const scanned = options.source !== "manual";
    setStatusFilter("all");
    setSearch(tracking);
    setScanCode(scanned ? tracking : "");
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const data = await lookupDeliveryOrder(token, tracking);
      const matchedStop = data?.order;
      if (!matchedStop) {
        throw new Error(`Aucune commande trouvee pour le tracking ${tracking}.`);
      }
      mergeStop(matchedStop);
      selectStop(matchedStop);
      setScanCode(scanned ? tracking : "");
      routeLookupRef.current = tracking;
      if (options.updateUrl !== false && tracking) {
        navigate(`/postman?tracking=${encodeURIComponent(tracking)}`, { replace: true });
      }
      setMessage(elakayAutoText("h_89770ad09a36e4", "Commande ouverte pour mise a jour{value0}.", { value0: tracking ? ` · ${tracking}` : "" }));
      window.setTimeout(() => {
        detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    } catch (lookupError) {
      setError(lookupError.message || elakayAutoText("h_36e86579bc85ac", "Aucune commande trouvee pour le tracking {value0}.", { value0: tracking || raw }));
    } finally {
      setLoading(false);
    }
  }, [mergeStop, navigate, selectStop, token]);

  useEffect(() => {
    const tracking = routeTracking.trim();
    if (!token || !tracking || routeLookupRef.current === tracking) return;
    routeLookupRef.current = tracking;
    findFromScan(tracking, { updateUrl: false, source: "manual" });
  }, [findFromScan, routeTracking, token]);

  useEffect(() => {
    if (!token || !openScannerFromRoute) return;
    const requestKey = routeLocation.search || "scan";
    if (routeScannerRef.current === requestKey) return;
    routeScannerRef.current = requestKey;
    setScannerOpen(true);
  }, [openScannerFromRoute, routeLocation.search, token]);

  const stopScanner = useCallback(() => {
    if (scannerTimerRef.current) {
      clearInterval(scannerTimerRef.current);
      scannerTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    detectingRef.current = false;
  }, []);

  useEffect(() => {
    if (!scannerOpen) {
      stopScanner();
      return undefined;
    }

    let cancelled = false;

    const startScanner = async () => {
      setScannerError("");
      if (!navigator.mediaDevices?.getUserMedia || !window.BarcodeDetector) {
        setScannerError("Detection camera indisponible. Utilisez le champ de recherche ou un lecteur externe.");
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        const detector = new window.BarcodeDetector();

        scannerTimerRef.current = setInterval(async () => {
          if (!videoRef.current || detectingRef.current) return;
          detectingRef.current = true;
          try {
            const result = await detector.detect(videoRef.current);
            if (result?.[0]?.rawValue) {
              stopScanner();
              setScannerOpen(false);
              await findFromScan(result[0].rawValue);
            }
          } catch {
            // Try again on the next camera frame.
          } finally {
            detectingRef.current = false;
          }
        }, 650);
      } catch {
        setScannerError("Camera refusee ou indisponible. Verifiez les permissions.");
      }
    };

    startScanner();
    return () => {
      cancelled = true;
      stopScanner();
    };
  }, [findFromScan, scannerOpen, stopScanner]);

  const locate = () => {
    if (!navigator.geolocation) {
      setError(elakayAutoText("h_e1fae1fbcc53ed", "La geolocalisation n'est pas disponible."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => setLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      }),
      (locationError) => setError(locationError.message || elakayAutoText("h_8f328f6321e51f", "Position indisponible.")),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
    );
  };

  const addPhotos = (event) => {
    const files = Array.from(event.target.files || [])
      .filter((file) => file.type.startsWith("image/"))
      .slice(0, Math.max(0, 4 - photos.length));
    const next = files.map((file) => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      file,
      url: URL.createObjectURL(file)
    }));
    setPhotos((current) => {
      const updated = [...current, ...next];
      photoRef.current = updated;
      return updated;
    });
    event.target.value = "";
  };

  const removePhoto = (id) => {
    setPhotos((current) => {
      const target = current.find((photo) => photo.id === id);
      if (target) URL.revokeObjectURL(target.url);
      const updated = current.filter((photo) => photo.id !== id);
      photoRef.current = updated;
      return updated;
    });
  };

  const saveStop = async () => {
    if (!selected) return;
    if (status === "delivered" && !recipientName.trim()) {
      setError(elakayAutoText("h_15cd332b93ee49", "Le nom du destinataire est requis."));
      return;
    }
    if (["attempted", "exception", "held"].includes(status) && !exceptionReason.trim()) {
      setError(elakayAutoText("h_c7fd25abe82f65", "Indiquez la raison de la tentative ou de l'incident."));
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const formData = new FormData();
      formData.append("status", status);
      formData.append("source", scanCode ? "scanner" : "manual");
      formData.append("scanCode", scanCode);
      formData.append("recipientName", recipientName);
      formData.append("eventNote", note);
      formData.append("exceptionReason", exceptionReason);
      formData.append("locationLabel", addressText(selected.shippingAddress));
      formData.append("occurredAt", new Date().toISOString());
      formData.append("carrierCode", selected.delivery?.carrierCode || "");
      formData.append("carrierName", selected.delivery?.carrierName || selected.shipmentInfo?.carrier || "Local delivery");
      formData.append("carrierType", selected.delivery?.carrierType || "local");
      formData.append("trackingNumber", selected.delivery?.trackingNumber || selected.shipmentInfo?.tracking || "");
      formData.append("accessTracking", selected.delivery?.trackingNumber || selected.shipmentInfo?.tracking || scanCode);
      formData.append("handlerRole", handlerRole);
      formData.append("proofKind", status === "delivered" ? "door" : "package");
      formData.append("proofKinds", JSON.stringify(photos.map(() => status === "delivered" ? "door" : "package")));
      formData.append("proofNote", note);

      if (location) {
        formData.append("latitude", location.latitude);
        formData.append("longitude", location.longitude);
        formData.append("accuracy", location.accuracy);
      }
      photos.forEach((photo) => formData.append("proofs", photo.file));

      const data = await updateDeliveryOrder(
        token,
        selected.orderId,
        selected.orderProductId,
        formData
      );

      photos.forEach((photo) => URL.revokeObjectURL(photo.url));
      photoRef.current = [];
      setPhotos([]);
      setSelected(data.order);
      mergeStop(data.order);
      setMessage(status === elakayAutoText("h_f818910a3e181b", "delivered") ? elakayAutoText("h_d354bb6bec9cb6", "Livraison confirmee.") : elakayAutoText("h_76d5b7097c608f", "Arret mis a jour."));
      await loadStops(search);
    } catch (saveError) {
      setError(saveError.message || elakayAutoText("h_c6dbb7e405f3cb", "Impossible d'enregistrer cet arret."));
    } finally {
      setSaving(false);
    }
  };

  const submitSearch = async (event) => {
    event.preventDefault();
    await findFromScan(search, { source: "manual" });
  };

  return (
    <Layout
      infoSource={infoSource}
      title={elakayAutoText("h_e67b8b2b52370a", "Postman")}
      description={elakayAutoText("h_fc50566bce98d4", "Tournee mobile des livreurs")}
      className="container-fluid"
      SliderPunShow={false}
      loading={false}
      {...infoSource?.allSettings?.layout}
    >
      <main className="postman-page">
        <div className="postman-shell">
          <header className="postman-header">
            <div>
              <h1>{elakayAutoText("h_667444b0e6ed62", "Postman Route")}</h1>
              <p>{elakayAutoText("h_e007e57a015168", "Scans, navigation, appels, preuves et statuts depuis le terrain.")}</p>
            </div>
            <div className="postman-operator">
              <label htmlFor="postman-role">{elakayAutoText("h_279809dbe9a8a9", "Mode operateur")}</label>
              <select
                id="postman-role"
                className="postman-select"
                value={handlerRole}
                onChange={(event) => setHandlerRole(event.target.value)}
              >
                <option value="driver">{elakayAutoText("h_4c9dda027c9fe9", "Livreur")}</option>
                <option value="company">{elakayAutoText("h_4021443eb40e55", "Compagnie de livraison")}</option>
              </select>
              <div className="postman-shift"><span className="postman-dot" />{elakayAutoText("h_008b2e34c0b6d8", "Tournee active")}</div>
            </div>
          </header>

          <section className="postman-summary">
            <div className="postman-summary-card"><span>{elakayAutoText("h_665115d8a6964b", "Arrets actifs")}</span><strong>{activeStops.length}</strong></div>
            <div className="postman-summary-card"><span>{elakayAutoText("h_c296e9499783ab", "En livraison")}</span><strong>{summary.outForDelivery || 0}</strong></div>
            <div className="postman-summary-card"><span>{elakayAutoText("h_612cda92a2b0e4", "Livres")}</span><strong>{summary.delivered || 0}</strong></div>
            <div className="postman-summary-card"><span>{elakayAutoText("h_5e326b17d2aa7f", "Incidents")}</span><strong>{summary.exceptions || 0}</strong></div>
          </section>

          <form className="postman-tools" onSubmit={submitSearch}>
            <div className="postman-search">
              <FiSearch />
              <input
                className="postman-input"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={elakayAutoText("h_4dbe6f2a2c28a6", "Commande, tracking ou scan...")}
              />
            </div>
            <select
              className="postman-select"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">{elakayAutoText("h_5641d4fd68513c", "Toute la tournee")}</option>
              <option value="picked_up">{elakayAutoText("h_b941a75321d4c7", "Collectes")}</option>
              <option value="accepted">{elakayAutoText("h_bc311143966c36", "Acceptes")}</option>
              <option value="out_for_delivery">{elakayAutoText("h_c296e9499783ab", "En livraison")}</option>
              <option value="in_transit">{elakayAutoText("h_7077e511761453", "En route")}</option>
              <option value="attempted">{elakayAutoText("h_1f31ea556d2995", "Tentatives")}</option>
              <option value="delivered">{elakayAutoText("h_612cda92a2b0e4", "Livres")}</option>
              <option value="exception">{elakayAutoText("h_5e326b17d2aa7f", "Incidents")}</option>
              <option value="held">{elakayAutoText("h_11fdfbaa7486c3", "Retenus")}</option>
            </select>
            <button type="button" className="postman-button postman-button-primary" onClick={() => setScannerOpen(true)}>
              <FiCamera />{elakayAutoText("h_28cba55d2aadf5", "Scan")}</button>
          </form>

          {scannerOpen && (
            <div className="postman-scanner">
              <div className="postman-scanner-stage">
                <video ref={videoRef} muted playsInline />
                {!scannerError && (
                  <div className="postman-scanner-frame">
                    <div className="postman-scanner-line" />
                  </div>
                )}
              </div>
              <div className="postman-scanner-footer">
                <span>{scannerError || elakayAutoText("h_1a604679836355", "Cadrez le QR code ou code-barres.")}</span>
                <button type="button" className="postman-button postman-button-secondary" onClick={() => setScannerOpen(false)}>
                  <FiX />{elakayAutoText("h_5ab4ec640d654d", "Fermer")}</button>
              </div>
            </div>
          )}

          {error && <div className="postman-alert postman-alert-error"><FiAlertTriangle /> {error}</div>}
          {message && <div className="postman-alert postman-alert-success"><FiCheckCircle /> {message}</div>}

          <div className="postman-layout">
            <section className="postman-panel">
              <div className="postman-panel-header">
                <div><strong>{elakayAutoText("h_dff21aa0e20178", "Arrets")}</strong><br /><span>{loading ? elakayAutoText("h_a209b664efa2dc", "Chargement...") : `${stops.length} commande(s)`}</span></div>
                <button type="button" className="postman-button postman-button-secondary" onClick={() => loadStops(search)}>
                  <FiRefreshCw className={loading ? "postman-spin" : ""} />
                </button>
              </div>
              <div className="postman-stop-list">
                {stops.length ? stops.map((stop, index) => {
                  const active = selected
                    && String(selected.orderId) === String(stop.orderId)
                    && String(selected.orderProductId) === String(stop.orderProductId);
                  return (
                    <button
                      type="button"
                      className={`postman-stop${active ? " is-active" : ""}`}
                      key={`${stop.orderId}_${stop.orderProductId}`}
                      onClick={() => selectStop(stop)}
                    >
                      <span className="postman-stop-number">{index + 1}</span>
                      <span style={{ minWidth: 0 }}>
                        <span className="postman-stop-name">{customerName(stop.shippingAddress)}</span>
                        <span className="postman-stop-address">{addressText(stop.shippingAddress)}</span>
                      </span>
                      <StatusPill value={stop.delivery?.status} />
                    </button>
                  );
                }) : (
                  <div className="postman-empty">
                    <div><FiPackage size={27} /><p>{elakayAutoText("h_199f4f20e4951e", "Aucun arret pour ce filtre.")}</p></div>
                  </div>
                )}
              </div>
            </section>

            <section className="postman-panel" ref={detailRef}>
              {!selected ? (
                <div className="postman-empty" style={{ minHeight: 480 }}>
                  <div><FiNavigation size={31} /><p>{elakayAutoText("h_3c008a5207b849", "Selectionnez ou scannez votre prochain arret.")}</p></div>
                </div>
              ) : (
                <div className="postman-detail">
                  <div className="postman-customer">
                    <div>
                      <h2>{customerName(selected.shippingAddress)}</h2>
                      <p>{addressText(selected.shippingAddress)}</p>
                      <p>{elakayAutoText("h_666aceab2998e6", "Commande #")}{shortId(selected.orderId)}</p>
                      <span className="postman-access-badge">
                        {operatorLabel(selected.access, handlerRole)}
                      </span>
                    </div>
                    <div className="postman-customer-actions">
                      {selected.shippingAddress?.tel && (
                        <a className="postman-round-action" href={`tel:${selected.shippingAddress.tel}`} aria-label={elakayAutoText("h_af0b4da258a6b0", "Appeler le client")}>
                          <FiPhone />
                        </a>
                      )}
                      <a
                        className="postman-round-action"
                        href={mapsUrl(selected.shippingAddress)}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={elakayAutoText("h_81126db8a40ee3", "Ouvrir la navigation")}
                      >
                        <FiNavigation />
                      </a>
                    </div>
                  </div>

                  <div className="postman-package">
                    <img src={selected.product?.image || infoSource?.imageEmptyelakay} alt="" />
                    <div>
                      <strong>{selected.product?.name}</strong>
                      <span>
                        {selected.product?.quantity || 1}{elakayAutoText("h_82972fd5cb2427", "colis/article(s)")}{" · "}{selected.delivery?.carrierName || selected.shipmentInfo?.carrier || elakayAutoText("h_053abf537093ae", "Livraison locale")}
                      </span>
                      <span>{elakayAutoText("h_837a0809fd4166", "Tracking:")}{selected.delivery?.trackingNumber || selected.shipmentInfo?.tracking || elakayAutoText("h_cfdc9551855f94", "Non defini")}</span>
                    </div>
                  </div>

                  <section className="postman-section">
                    <div className="postman-section-title"><FiTruck />{elakayAutoText("h_c67150a2021526", "Statut rapide")}</div>
                    <div className="postman-status-grid">
                      {POSTMAN_STATUSES.map(({ value, label, icon: Icon }) => (
                        <button
                          type="button"
                          className={`postman-status-button${status === value ? " is-active" : ""}`}
                          key={value}
                          onClick={() => setStatus(value)}
                        >
                          <Icon /> {label}
                        </button>
                      ))}
                    </div>
                  </section>

                  <section className="postman-section">
                    <div className="postman-section-title"><FiUser />{elakayAutoText("h_4850583022c524", "Details de l'arret")}</div>
                    <div className="postman-form-grid">
                      <div className="postman-field">
                        <label>{elakayAutoText("h_56579042c7742c", "Destinataire")}</label>
                        <input className="postman-input" value={recipientName} onChange={(event) => setRecipientName(event.target.value)} />
                      </div>
                      <div className="postman-field">
                        <label>{elakayAutoText("h_58f4bb41e59f75", "Code scanne")}</label>
                        <input className="postman-input" value={scanCode} onChange={(event) => setScanCode(event.target.value)} />
                      </div>
                      {["attempted", "exception", "held"].includes(status) && (
                        <div className="postman-field postman-field-full">
                          <label>{elakayAutoText("h_c14900eab6d5f3", "Raison")}</label>
                          <input
                            className="postman-input"
                            value={exceptionReason}
                            onChange={(event) => setExceptionReason(event.target.value)}
                            placeholder={elakayAutoText("h_63b97f6f28eff9", "Client absent, adresse introuvable, colis endommage...")}
                          />
                        </div>
                      )}
                      <div className="postman-field postman-field-full">
                        <label>{elakayAutoText("h_30d06748f8b1ce", "Note de l'operateur")}</label>
                        <textarea
                          className="postman-textarea"
                          value={note}
                          onChange={(event) => setNote(event.target.value)}
                          placeholder={elakayAutoText("h_72108e888801ed", "Porte, concierge, instruction, etat du colis...")}
                        />
                      </div>
                    </div>
                    <button type="button" className="postman-button postman-button-secondary" style={{ marginTop: 8 }} onClick={locate}>
                      <FiMapPin />{elakayAutoText("h_9c74ad2465d239", "Ajouter ma position")}</button>
                    {location && (
                      <div className="postman-location">
                        <FiMapPin /> {location.latitude.toFixed(5)}, {location.longitude.toFixed(5)} · {Math.round(location.accuracy || 0)} m
                      </div>
                    )}
                  </section>

                  <section className="postman-section">
                    <div className="postman-section-title"><FiCamera />{elakayAutoText("h_5d39d7c1f27036", "Photos de preuve")}</div>
                    <div className="postman-photo-row">
                      {photos.map((photo) => (
                        <div className="postman-photo" key={photo.id}>
                          <img src={photo.url} alt={elakayAutoText("h_4d3971af67a96f", "Preuve")} />
                          <button type="button" onClick={() => removePhoto(photo.id)}><FiX /></button>
                        </div>
                      ))}
                      {photos.length < 4 && (
                        <label className="postman-upload">
                          <span><FiUpload size={21} /><span>{elakayAutoText("h_49b3ea5830269b", "Prendre une photo")}</span></span>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                            capture="environment"
                            multiple
                            onChange={addPhotos}
                          />
                        </label>
                      )}
                    </div>
                  </section>

                  <section className="postman-section">
                    <div className="postman-section-title"><FiClock />{elakayAutoText("h_8e7cf0f7f1de04", "Historique de livraison")}</div>
                    <div className="postman-timeline">
                      {selected.delivery?.events?.length ? (
                        selected.delivery.events.slice(0, 10).map((event, index) => (
                          <div className="postman-timeline-item" key={event._id || `${event.occurredAt}_${index}`}>
                            <span className={`postman-timeline-dot postman-timeline-dot-${statusMeta(event.status).tone}`} />
                            <div>
                              <strong>{statusMeta(event.status).label}</strong>
                              <span>
                                {formatEventDate(event.occurredAt)}
                                {event.actorName ? ` · ${event.actorName}` : ""}
                                {event.actorRole ? ` · ${event.actorRole}` : ""}
                              </span>
                              {(event.note || event.exceptionReason) && (
                                <p>{event.note || event.exceptionReason}</p>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="postman-timeline-empty">{elakayAutoText("h_4c75c6657fcc15", "Aucun scan enregistre.")}</p>
                      )}
                    </div>
                  </section>

                  <div className="postman-save">
                    <button type="button" className="postman-button postman-button-primary" onClick={saveStop} disabled={saving}>
                      {saving ? <><FiRefreshCw className="postman-spin" />{elakayAutoText("h_9bf1058a71405a", "Enregistrement...")}</> : <><FiCheckCircle />{elakayAutoText("h_d724749a4c137d", "Valider cet arret")}</>}
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </Layout>
  );
}

export default Postman;
